<template>
  <v-data-table-virtual
    fixed-header
    :headers="sortedHeaders"
    :items="items"
    :search="search"
    item-value="id"
    data-testid="yard-view-list-view-table"
    show-expand
    v-model:expanded="expanded"
    :loading="isLoading">
    <template #header.assetCode="props">
      <v-icon size="small">mdi-truck-trailer</v-icon>
      {{ props.column.title }}

      <v-icon v-if="isFieldKeySorted(props.sortBy, props.column.key)">
        {{ getSortIcon(props.sortBy, props.column.key) }}
      </v-icon>
    </template>

    <template #header.carrierCompany="props">
      <v-icon size="small">mdi-truck-flatbed</v-icon>
      {{ props.column.title }}

      <v-icon v-if="isFieldKeySorted(props.sortBy, props.column.key)">
        {{ getSortIcon(props.sortBy, props.column.key) }}
      </v-icon>
    </template>

    <template #expanded-row="{ columns, item }">
      <tr class="bg-disabled">
        <td :colspan="columns.length" class="py-6 px-12">
          <h3>{{ t('yard.pages.yardView.headers.checkInDetails') }}</h3>
          <v-divider class="my-2" />
          <template v-if="item.assetVisit">
            <asset-visit-form-data
              variant="grid"
              :asset-visit="item.assetVisit"
              :warehouse="selectedWarehouse" />
          </template>
          <template v-else>
            <p>{{ t('yard.pages.yardView.messages.noAssetData') }}</p>
          </template>
        </td>
      </tr>

      <tr class="bg-disabled">
        <td :colspan="columns.length" class="py-6 px-12">
          <h3>{{ t('yard.pages.yardView.headers.assetDetails') }}</h3>
          <v-divider class="my-2" />
          <template v-if="item.assetContainer">
            <key-value-list :is-divided="true" variant="list-stacked">
              <key-value
                :label="t('global.basicTerms.notes')"
                :value="item.assetContainer.notes || '--'" />
            </key-value-list>

            <custom-forms-multiple-trigger-form
              :warehouse-id="selectedWarehouse.id"
              :is-editable="true"
              :object-id="item.assetContainer.id"
              :triggers="assetContainerTriggers"
              :timezone="selectedWarehouse.timezone"
              :components-mapping="customFormsComponentsMapping"
              :military-time-enabled="$isMilitaryTimeEnabled(selectedWarehouse)" />
          </template>
          <template v-else>
            <p>{{ t('yard.pages.yardView.messages.noAssetData') }}</p>
          </template>
        </td>
      </tr>
    </template>

    <template #item.assetCode="{ item }">
      <span v-if="item.assetCode" v-html="highlightSearchResult(item.assetCode)"></span>
      <span v-else class="text__color-text-disabled">No asset</span>
    </template>

    <template #item.assetType="{ item }">
      <span v-if="item.assetType">
        <v-chip size="small" variant="tonal" color="secondary">
          <strong v-html="highlightSearchResult(item.assetType)"></strong>
        </v-chip>
      </span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierCompany="{ item }">
      <span v-if="item.carrierCompany" v-html="highlightSearchResult(item.carrierCompany)"></span>
      <span v-else class="text__color-text-disabled">No carrier</span>
    </template>

    <template #item.carrierVisitType="{ item }">
      <span v-if="item.carrierVisitType">
        <v-chip size="small" variant="tonal" color="secondary">
          <strong v-html="highlightSearchResult(item.carrierVisitType)"></strong>
        </v-chip>
      </span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.areaName="{ item }">
      <span v-if="item.areaName" v-html="highlightSearchResult(item.areaName)"></span>
      <span v-else class="text__color-text-disabled">
        {{ t('yard.messages.waitingAssignment') }}
      </span>
    </template>

    <template #item.spotCode="{ item }">
      <strong v-if="item.spotCode" v-html="highlightSearchResult(item.spotCode)"></strong>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierPhone="{ item }">
      <span v-if="item.carrierPhone" v-html="highlightSearchResult(item.carrierPhone)"></span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierArrivedAt="{ item }">
      <template v-if="item.carrierArrivedAt">
        <span v-html="formatDate(item.carrierArrivedAt)"></span>
        <p
          v-if="search"
          class="text-caption font-italic text-grey"
          v-html="highlightSearchResult(item.carrierArrivedAt)"></p>
      </template>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.assetArrivedAt="{ item }">
      <template v-if="item.assetArrivedAt">
        <span v-html="formatDate(item.assetArrivedAt)"></span>
        <p
          v-if="search"
          class="text-caption font-italic text-grey"
          v-html="highlightSearchResult(item.assetArrivedAt)"></p>
      </template>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierDaysAtYard="{ item }">
      <template v-if="item.carrierDaysAtYard">
        <span v-html="daysAtYard(item.carrierDaysAtYard)"></span>
        <p
          v-if="search"
          class="text-caption font-italic text-grey"
          v-html="highlightSearchResult(item.carrierDaysAtYard)"></p>
      </template>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.assetDaysAtYard="{ item }">
      <template v-if="item.assetDaysAtYard">
        <span v-html="daysAtYard(item.assetDaysAtYard)"></span>
        <p
          v-if="search"
          class="text-caption font-italic text-grey"
          v-html="highlightSearchResult(item.assetDaysAtYard)"></p>
      </template>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.refNumber="{ item }">
      <span v-if="item.refNumber" v-html="highlightSearchResult(item.refNumber)"></span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierPlanned="{ item }">
      <span v-if="item.carrierPlanned" v-html="highlightSearchResult(item.carrierPlanned)"></span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.carrierAppointmentIdentifier="{ item }">
      <span
        v-if="item.carrierAppointmentIdentifier"
        v-html="highlightSearchResult(item.carrierAppointmentIdentifier)"></span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.assetContainerStatus="{ item }">
      <span v-if="item.assetContainerStatus">
        <yard-view-asset-container-status-select
          class="table-select"
          :container-status="item.assetContainerStatus"
          entry-point="List View"
          :asset-container="item"
          @input="value => updateStatus(item.id, value)" />
      </span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.assetContainerState="{ item }">
      <span v-if="item.assetContainerState || item.assetContainerState === null">
        <yard-view-asset-container-state-select
          class="table-select"
          :container-state="item.assetContainerState"
          entry-point="List View"
          :asset-container="item"
          @input="value => updateAssetContainerState(item.id, value)" />
      </span>
      <span v-else class="text__color-text-disabled">-</span>
    </template>

    <template #item.actions="{ item }">
      <div class="d-flex flex-row">
        <v-btn
          :class="{ 'is-visibility-hidden': !item.spotId }"
          icon
          @click="goToSpot(item)"
          variant="text"
          size="small"
          :title="t('yard.pages.yardView.tooltips.goToSpot')">
          <v-icon>mdi-arrow-top-right</v-icon>
        </v-btn>
      </div>
    </template>
    <template #no-data>
      <p class="my-6">
        {{ t('yard.pages.yardView.messages.noCarrierOrAssetsFound') }}
      </p>
    </template>
  </v-data-table-virtual>
</template>

<script>
import { defineComponent, computed, ref } from 'vue';
import { first } from 'lodash';
import { LuxonDateTimeFormats, formatPhoneNumber, YardViewModeEnum } from '@nova/core';
import { useEventHub, useCustomForms } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';

import YardViewAssetContainerStatusSelect from '../Selects/YardViewAssetContainerStatusSelect.vue';
import YardViewAssetContainerStateSelect from '../Selects/YardViewAssetContainerStateSelect.vue';
import KeyValue from '@satellite/components/elements/key_value/KeyValue.vue';
import KeyValueList from '@satellite/components/elements/key_value/KeyValueList.vue';
import CustomFormsMultipleTriggerForm from '@satellite/components/elements/CustomFormsMultipleTriggerForm.vue';
import AssetVisitFormData from '@/components/elements/AssetVisitFormData.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewListViewTable',

  components: {
    YardViewAssetContainerStatusSelect,
    YardViewAssetContainerStateSelect,
    KeyValue,
    KeyValueList,
    CustomFormsMultipleTriggerForm,
    AssetVisitFormData
  },
  props: {
    headers: {
      type: Array,
      required: true
    },
    search: {
      type: String,
      default: ''
    },
    spots: {
      type: Array,
      default: () => [],
      required: true
    },
    waitingAssets: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const expanded = ref([]);
    const eventHub = useEventHub();
    const { customFormsComponentsMapping } = useCustomForms();

    const formatDate = dateTime =>
      yardStore.formatDateTime(
        dateTime,
        LuxonDateTimeFormats.ShortDayDateSlashed12Hour,
        LuxonDateTimeFormats.ShortDayDateSlashed24Hour
      );

    const formatSortableDate = dateTime =>
      yardStore.formatDateTime(dateTime, LuxonDateTimeFormats.ISO, LuxonDateTimeFormats.ISO);
    const daysAtYard = dateTime => yardStore.relativeTimeFromWarehouse(dateTime);

    const mapAssetVisit = assetVisit => {
      return {
        assetVisit,
        carrierCompany: assetVisit.company?.name ?? assetVisit.companyHint,
        carrierPhone: formatPhoneNumber(assetVisit.phone),
        carrierVisitType: yardStore.getAssetVisitTypeDisplayName(assetVisit.visitType),
        carrierArrivedAt: formatSortableDate(assetVisit.createDateTime),
        carrierDaysAtYard: formatSortableDate(assetVisit.createDateTime),
        carrierPlanned: assetVisit.isPlanned
          ? t('global.basicTerms.yes')
          : t('global.basicTerms.no'),
        carrierAppointmentIdentifier: assetVisit.driverAppointmentIdentifier,
        refNumber: assetVisit.appointment?.refNumber
      };
    };

    const mapAssetContainer = assetContainer => {
      return {
        assetContainer,
        id: assetContainer.id,
        assetCode: assetContainer.code,
        assetType: t(`core.yard.assetContainerTypes.${assetContainer.type.toLowerCase()}`),
        assetContainerState: assetContainer.state,
        assetContainerStatus: yardStore.getAssignedAssetContainerStatus(
          assetContainer.assetContainerEvents ?? []
        ),
        assetArrivedAt: formatSortableDate(assetContainer.createDateTime),
        assetDaysAtYard: formatSortableDate(assetContainer.createDateTime)
      };
    };

    const items = computed(() => {
      const items = [];

      props.waitingAssets.forEach(asset => {
        if (yardStore.isAssetContainer(asset)) {
          items.push(mapAssetContainer(asset));
        } else {
          if (asset.assetContainers.length > 0) {
            asset.assetContainers.forEach(assetContainer => {
              items.push({
                ...mapAssetContainer(assetContainer),
                ...mapAssetVisit(asset)
              });
            });
          } else {
            items.push({
              ...mapAssetVisit(asset),
              id: asset.id
            });
          }
        }
      });

      props.spots
        .filter(spot => {
          if (yardStore.selectedAreasRef.value.length > 0) {
            return yardStore.selectedAreasRef.value.includes(spot.spotAreaId);
          }
        })
        .forEach(spot => {
          if (!spot.spotAssignments?.length) {
            return;
          }

          const spotAssignment = first(spot.spotAssignments);
          const spotData = {
            spotId: spot.id,
            areaName: yardStore.spotAreasRef.value.find(area => area.id === spot.spotAreaId)?.name,
            spotCode: spot.code
          };

          if (spotAssignment.assetContainerId) {
            items.push({
              ...mapAssetContainer(spotAssignment.assetContainer),
              ...spotData
            });
          }

          if (spotAssignment.assetVisitId) {
            if (spotAssignment.assetVisit.assetContainers?.length > 0) {
              spotAssignment.assetVisit.assetContainers.forEach(assetContainer => {
                items.push({
                  ...mapAssetContainer(assetContainer),
                  ...mapAssetVisit(spotAssignment.assetVisit),
                  ...spotData
                });
              });
            } else {
              items.push({
                ...mapAssetVisit(spotAssignment.assetVisit),
                id: spotAssignment.assetVisit.id,
                ...spotData
              });
            }
          }
        });

      return items.sort((a, b) => {
        if (a.areaName && b.areaName) {
          return a.areaName.localeCompare(b.areaName);
        }
        if (a.spotCode && b.spotCode) {
          return a.spotCode.localeCompare(b.spotCode);
        }
        return 0;
      });
    });

    const sortedHeaders = computed(() => props.headers.sort((a, b) => a.order - b.order));

    const highlightSearchResult = text => {
      if (!props.search) {
        return text;
      }
      const regex = new RegExp(`(${props.search})`, 'gi');
      return text.replace(regex, `<mark>$1</mark>`);
    };

    const goToSpot = item => {
      yardStore.viewModeRef.value = YardViewModeEnum.GRID;

      const spot = yardStore.spotsRef.value.find(spot => spot.id === item.spotId);
      yardStore.selectedSpotRef.value = spot;

      yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.GO_TO_SPOT, {
        spot,
        assetVisit: item.assetVisit ?? item.assetContainer,
        entryPoint: 'Live Yard List View Table'
      });

      eventHub.$emit(yardStore.GO_TO_SPOT, {
        newSpotCode: spot.code
      });
    };

    const isFieldKeySorted = (sortedKeys, key) => {
      return Boolean(sortedKeys.find(s => s.key === key));
    };

    const getSortIcon = (sortedKeys, key) => {
      if (isFieldKeySorted(sortedKeys, key)) {
        return sortedKeys.find(s => s.key === key).order === 'desc'
          ? 'mdi-arrow-down'
          : 'mdi-arrow-up';
      }
    };

    const updateAssetContainerState = async (assetContainerId, state) =>
      yardStore
        .updateAssetContainer(assetContainerId, { state })
        .then(assetContainer => eventHub.$emit('update-AssetContainer', assetContainer));

    return {
      t,
      isLoading: yardStore.isLoadingAssetVisitsRef,
      expanded,
      sortedHeaders,
      items,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      assetContainerTriggers: yardStore.assetContainerTriggersRef.value || [],
      updateStatus: yardStore.updateAssetContainerStatus,
      updateAssetContainerState,
      goToSpot,
      highlightSearchResult,
      isFieldKeySorted,
      customFormsComponentsMapping,
      getSortIcon,
      formatDate,
      daysAtYard
    };
  }
});
</script>
<style lang="scss">
.yard-view-list-view {
  .table-select {
    overflow-x: auto;
    padding-left: 2px;
    padding-right: 2px;
    .v-field__append-inner {
      display: none;
    }
  }
}
</style>
